import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {Select, Empty, Modal, Checkbox, Radio, message} from 'antd';
import api from '../Interceptor';
import {getRoute} from '../api/routes';
import CustomModal from '../components/customModal/customModal';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faRefresh} from '@fortawesome/free-solid-svg-icons';
import ModalFooter from '../components/customModal/modalFooter';
import {CustomInput} from '../components/ui';
import {validateFormHandler} from '../utils/validations/validateForm';
const {Option} = Select;

const SuperUsersList = () => {
  const [status, setStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [userList, setUserList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationSubject, setNotificationSubject] = useState('');
  const [notificationText, setNotificationText] = useState('');
  const [notificationType, setNotificationType] = useState('payment');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const fetchUser = async () => {
    setLoading(true);
    try {
      const {data: userList} = await api.get(
        getRoute('users', 'superadmin', 'admin', false, true, 'list')
      );
      setUserList(userList);
    } catch (error) {
      if (error.response?.status === 401) {
        message.error('Unauthorized access. Please log in again.');
      } else {
        message.error('Failed to fetch user list');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const updateStatus = async (adminId, currentStatus) => {
    try {
      const newStatus =
        currentStatus === process.env.REACT_APP_STATUS_ACTIVE
          ? process.env.REACT_APP_STATUS_INACTIVE
          : process.env.REACT_APP_STATUS_ACTIVE;

      const response = await api.patch(
        getRoute('users', 'superadmin', 'admin', false, false, 'updateStatus'),
        {
          adminId,
          statusId: newStatus,
        }
      );

      if (response.status === 200) {
        toast.success('User status updated successfully!');
        fetchUser();
      }
    } catch (error) {
      toast.error('Failed to update status.');
      console.error('Error updating status:', error);
    }
  };

  const updatePaymentStatus = async (adminId, currentStatus) => {
    try {
      const newStatus =
        currentStatus === process.env.REACT_APP_PAID_SUBSCRIPTION_STATUS_ID
          ? process.env.REACT_APP_UNPAID_SUBSCRIPTION_STATUS_ID
          : process.env.REACT_APP_PAID_SUBSCRIPTION_STATUS_ID;

      const response = await api.patch(
        getRoute(
          'users',
          'superadmin',
          'admin',
          false,
          false,
          'updatePaymentStatus'
        ),
        {
          adminId,
          statusId: newStatus,
        }
      );

      if (response.status === 200) {
        toast.success('User payment status updated successfully!');
        fetchUser();
      }
    } catch (error) {
      toast.error('Failed to update payment status.');
      console.error('Error updating payment status:', error);
    }
  };

  const sendNotification = async (title, description) => {
    try {
      const response = await api.post(
        getRoute('users', 'superadmin', 'notification', false, false, ''),
        {
          title,
          description,
        }
      );

      if (response.status === 201) {
        return response.data;
      }
      toast.success('Notification Sent successfully');
    } catch (error) {
      toast.error('Failed to send notification.');
    }
  };

  const assignNotificationToUsers = async (notificationId, users) => {
    try {
      const response = await api.post(
        getRoute(
          'users',
          'superadmin',
          'admin',
          false,
          false,
          'assignNotification'
        ),
        {
          id: notificationId,
          users,
        }
      );

      if (response.status === 200) {
        toast.success('Notification assigned successfully!');
      }
    } catch (error) {
      toast.error('Failed to assign notification.');
      console.error('Error assigning notification:', error);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    try {
      let title, description;
      if (notificationType === 'payment') {
        title = 'Payment';
        description = 'Please complete your payment.';
      } else {
        title = notificationSubject;
        description = notificationText;
      }
      if (notificationType === 'custom') {
        const validationErrors = validateFormHandler({
          title: notificationSubject,
          description: notificationText,
        });
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          return;
        }
      }
      const notificationId = await sendNotification(title, description);
      if (notificationId) {
        await assignNotificationToUsers(notificationId, selectedUsers);
      }

      setIsNotificationOpen(false);
      setNotificationSubject('');
      setNotificationText('');
      setSelectedUsers([]);
    } catch (error) {
      toast.error('Failed to send notification.');
      console.error('Error sending notification:', error);
    }
  };

  const openNotificationModal = () => {
    setIsNotificationOpen(true);
  };

  const handleSelectAllUsers = (e) => {
    if (e.target.checked) {
      setSelectedUsers(filteredUsers.map((user) => user._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers((prevSelectedUsers) =>
      prevSelectedUsers.includes(userId)
        ? prevSelectedUsers.filter((id) => id !== userId)
        : [...prevSelectedUsers, userId]
    );
  };

  const filteredUsers = userList.filter((user) => {
    const statusMatch = status ? user.status === status : true;
    const paymentMatch = paymentStatus
      ? user.paymentStatus === paymentStatus
      : true;
    return statusMatch && paymentMatch;
  });

  return (
    <div className="rounded-xl bg-white h-full py-3 px-4 border-2 border-[#E9EAEB] font-customFont">
      <div className="flex justify-between items-center mb-4">
        <div style={{fontFamily: 'Noto Serif, sans-serif'}}>
          <h1 className="font-bold text-lg md:text-xl">Users</h1>
          <p className="text-xs text-gray-500 font-semibold">
            View and manage your users
          </p>
        </div>
        <button
          className="text-black border border-[#DCE3E3] px-3 py-1 rounded-md flex items-center"
          onClick={() => {
            setStatus('');
            setPaymentStatus('');
          }}
        >
          <FontAwesomeIcon icon={faRefresh}></FontAwesomeIcon>
        </button>
      </div>

      <div className="mb-4 flex justify-between">
        <div className="flex gap-2 w-1/3">
          <Select
            value={status}
            onChange={setStatus}
            className="w-full rounded-md"
          >
            <Option value="">Status</Option>
            <Option value={process.env.REACT_APP_STATUS_ACTIVE}>Active</Option>
            <Option value={process.env.REACT_APP_STATUS_INACTIVE}>
              Inactive
            </Option>
          </Select>
          <Select
            value={paymentStatus}
            onChange={(value) => setPaymentStatus(value)}
            className="w-full rounded-md"
          >
            <Option value="">Payment Status</Option>
            <Option value={process.env.REACT_APP_PAID_SUBSCRIPTION_STATUS_ID}>
              Paid
            </Option>
            <Option value={process.env.REACT_APP_UNPAID_SUBSCRIPTION_STATUS_ID}>
              Unpaid
            </Option>
          </Select>
        </div>
        {selectedUsers.length > 0 && (
          <button
            type="default"
            className="bg-themeGradient rounded-lg text-white py-2 px-3"
            onClick={openNotificationModal}
          >
            Send Notification
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-full">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="relative">
          <div className="max-h-[500px] scrollbar-hide">
            <div className="overflow-x-auto overflow-hidden rounded-lg border border-gray-200">
              <div className="min-w-[800px] sm:min-w-full rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[#E9EAEB] text-[#535862] font-semibold">
                    <tr>
                      <th className="py-3 px-4 text-left">
                        <Checkbox
                          onChange={handleSelectAllUsers}
                          checked={
                            selectedUsers.length > 0 &&
                            filteredUsers.length > 0 &&
                            selectedUsers.length === filteredUsers.length
                          }
                          indeterminate={
                            selectedUsers.length > 0 &&
                            selectedUsers.length < filteredUsers.length
                          }
                        />
                      </th>
                      <th className="py-3 px-4 text-left">User</th>
                      <th className="py-3 px-4 text-left">Email</th>
                      <th className="py-3 px-4 text-left">Plan</th>
                      <th className="py-3 px-4 text-left">
                        Subscription Start Date
                      </th>
                      <th className="py-3 px-4 text-left">
                        Subscription End Date
                      </th>
                      <th className="py-3 px-4 text-left">Payment Status</th>
                      <th className="py-3 px-4 text-left">Last Login</th>
                      <th className="py-3 px-4 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <tr
                          key={user.id}
                          className="hover:bg-gray-50 transition-colors duration-200 border-t bg-white divide-y divide-gray-200 text-[#535862]"
                        >
                          <td className="py-4 px-4">
                            <Checkbox
                              checked={selectedUsers.includes(user._id)}
                              onChange={() => handleUserSelection(user._id)}
                            />
                          </td>
                          <td className="py-4 px-4">{user.name}</td>
                          <td className="py-4 px-4">{user.email}</td>
                          <td className="py-4 px-4">
                            {user.subscription?.plan?.name
                              ? user.subscription.plan.name
                              : '-'}
                          </td>
                          <td className="py-4 px-4">
                            {/* Subscription Start Date */}
                            {user.subscription?.updatedAt
                              ? new Date(
                                  user.subscription.updatedAt
                                ).toLocaleDateString('en-GB') // dd/mm/yyyy
                              : 'N/A'}
                          </td>
                          <td className="py-4 px-4">
                            {user.subscription?.updatedAt &&
                            user.subscription?.plan?.durationDays
                              ? new Date(
                                  new Date(
                                    user.subscription.updatedAt
                                  ).getTime() +
                                    user.subscription.plan.durationDays *
                                      24 *
                                      60 *
                                      60 *
                                      1000
                                ).toLocaleDateString('en-GB')
                              : '-'}
                          </td>
                          <td className="py-4 px-4">
                            <span
                              className={`inline-flex items-start px-2.5 py-0.5 rounded-full text-sm font-medium ${
                                {
                                  paid: 'bg-green-100 text-green-800',
                                  unpaid: 'bg-red-100 text-red-800',
                                  trial: 'bg-blue-100 text-blue-800',
                                  retry: 'bg-yellow-100 text-yellow-800',
                                }[
                                  user.subscription?.subscriptionStatus?.name
                                ] || 'bg-gray-100 text-gray-800'
                              }
`}
                            >
                              {user.subscription?.subscriptionStatus?.name
                                ? user.subscription.subscriptionStatus.name
                                    .charAt(0)
                                    .toUpperCase() +
                                  user.subscription.subscriptionStatus.name.slice(
                                    1
                                  )
                                : 'N/A'}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            {user.lastLogin
                              ? new Date(user.lastLogin).toLocaleDateString(
                                  'en-GB'
                                )
                              : '-'}
                          </td>

                          <td className="py-4 px-4 text-center">
                            <div className="flex items-center">
                              <div
                                className={`relative inline-block w-12 h-6  transition duration-200 ease-linear rounded-full cursor-pointer ${
                                  user.status ===
                                  process.env.REACT_APP_STATUS_ACTIVE
                                    ? 'bg-green-500'
                                    : 'bg-gray-400'
                                }`}
                                onClick={() =>
                                  updateStatus(user._id, user.status)
                                }
                              >
                                <span
                                  className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                    user.status ===
                                    process.env.REACT_APP_STATUS_ACTIVE
                                      ? 'translate-x-6'
                                      : 'translate-x-0'
                                  }`}
                                ></span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="py-5">
                          <Empty description="No Users Found" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <CustomModal
        modalTitle="Send Notification"
        isOpen={isNotificationOpen}
        onClose={() => {
          setIsNotificationOpen(false);
          setErrors({});
        }}
      >
        <form onSubmit={(e) => handleSendNotification(e)}>
          <Radio.Group
            value={notificationType}
            onChange={(e) => setNotificationType(e.target.value)}
          >
            <Radio value="payment">Payment Alert</Radio>
            <Radio value="custom">Custom Alert</Radio>
          </Radio.Group>
          {notificationType === 'custom' && (
            <div className="mt-2">
              <CustomInput
                label="Subject"
                placeholder="Enter Notification subject"
                value={notificationSubject}
                name="title"
                onChange={(e) => {
                  setNotificationSubject(e.target.value);
                  if (errors.title) {
                    setErrors((prev) => ({...prev, title: undefined}));
                  }
                }}
                message={errors.title}
              />
              <CustomInput
                as="textarea"
                label="Notification Text"
                rows={4}
                name="description"
                placeholder="Enter your reminder message"
                value={notificationText}
                message={errors.description}
                onChange={(e) => {
                  setNotificationText(e.target.value);
                  if (errors.description) {
                    setErrors((prev) => ({...prev, description: undefined}));
                  }
                }}
              />
            </div>
          )}
          <ModalFooter
            onCancel={() => {
              setIsNotificationOpen(false);
              setErrors({});
            }}
            isLoading={loading}
          />
        </form>
      </CustomModal>
    </div>
  );
};

export default SuperUsersList;
